import React, { useState, useRef, useEffect } from 'react';
import { Send, Upload, Video, Clock, CheckCircle, Download, Zap, Cpu, AlertCircle, Plus, Sparkles } from 'lucide-react';
import SimpleVideoPreview from './SimpleVideoPreview';
import { FlaskConical } from 'lucide-react'; // icône labo (beta)


// NOTE: 
// - Tries to load @ffmpeg/core-mt (multi-thread, requires crossOriginIsolated: COOP/COEP headers)
// - If not available or fails, falls back to single-thread @ffmpeg/core (no worker required)
// - Uses CDN + toBlobURL() to avoid CORS and bundler path issues 

const DEFAULT_CF_BASE_US = "https://us-central1-lvmh-editor.cloudfunctions.net";
const INTRO_OUTRO_URL = "https://pub-5fa6b6e240814e96bc2f966cc1a5e705.r2.dev/intro-2.mp4";


// FFmpeg WASM loader (global singleton)
let ffmpeg = null;
let ffmpegLoaded = false;

// ⚡ Fonction stable loadFFmpeg
const loadFFmpeg = async () => {
  if (ffmpegLoaded) return ffmpeg;

  try {
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');

    ffmpeg = new FFmpeg();

    // ✅ logger attaché après instanciation
    if (ffmpeg?.on) {
      ffmpeg.on('log', ({ type, message }) => {
        if (type === 'error') console.error('[ffmpeg]', message);
      });
    }

    const tryLoad = async (useMT) => {
      const baseURL = useMT
        ? 'https://cdn.jsdelivr.net/npm/@ffmpeg/core-mt@0.12.10/dist/umd'
        : 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';

      const options = {
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      };
      if (useMT) {
        options.workerURL = await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript');
      }
      await ffmpeg.load(options);
    };

    try {
      if (self.crossOriginIsolated) {
        await tryLoad(true); // multi-thread
      } else {
        console.warn('[FFmpeg] crossOriginIsolated=false → single-thread core');
        await tryLoad(false);
      }
    } catch (e) {
      console.warn('[FFmpeg] core-mt load failed, fallback single-thread:', e);
      await tryLoad(false);
    }

    ffmpegLoaded = true;
    return ffmpeg;
  } catch (error) {
    console.error('FFmpeg load error:', error);
    throw new Error('Impossible de charger FFmpeg WASM');
  }
};


  // --- Extraction audio avec FFmpeg WASM ---
 // /src/components/AIVideoChat.jsx

// --- Extraction audio avec FFmpeg WASM ---
const extractAudio = async (videoFile, fmt = 'mp3') => {
  if (!ffmpegLoaded) await loadFFmpeg();
  // ⛔️ La ligne problématique avec `fetchFile` est retirée d'ici.
  // const { fetchFile } = await import('@ffmpeg/util');

  const inName = 'input.mp4';
  
  // ✅ FIX: On lit le fichier en ArrayBuffer AVANT de le passer à FFmpeg,
  // ce qui évite l'erreur WebKitBlobResource.
  const fileData = await videoFile.arrayBuffer();
  await ffmpeg.writeFile(inName, new Uint8Array(fileData));

  let outName = 'audio.mp3';
  let args = [
    '-i', inName,
    '-vn', '-ac', '1', '-ar', '16000',
    '-c:a', 'libmp3lame', '-b:a', '64k',
    '-y', outName,
  ];

  try {
    await ffmpeg.exec(args);
  } catch {
    console.warn('MP3 encoding failed, falling back to WAV.');
    outName = 'audio.wav';
    args = ['-i', inName, '-vn', '-ac', '1', '-ar', '16000', '-c:a', 'pcm_s16le', '-y', outName];
    await ffmpeg.exec(args);
    fmt = 'wav';
  }

  const data = await ffmpeg.readFile(outName);
  const mime = fmt === 'mp3' ? 'audio/mpeg' : 'audio/wav';
  const blob = new Blob([data.buffer], { type: mime });
  const url = URL.createObjectURL(blob);

  await ffmpeg.deleteFile(inName);
  await ffmpeg.deleteFile(outName);

  return { blob, url, fmt };
};
  
 const uploadAudioToR2 = async (blob, ext = 'mp3') => {
  const resp = await fetch(`${DEFAULT_CF_BASE_US}/getUploadUrl`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: `chat-audio-${Date.now()}.${ext}`,
      prefix: 'chat-audio',
    }),
  }).then((r) => r.json());

  if (!resp.ok) throw new Error(resp.error || 'getUploadUrl failed');

  await fetch(resp.putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': blob.type },
    body: blob,
  });

  // ⬇️ on renvoie les deux
  return {
    publicUrl: resp.publicUrl || null, // pour l’UI si vraiment public
    getUrl: resp.getUrl,               // signé GET (toujours OK côté backend)
    key: resp.key,
  };
};

// — Durée vidéo locale
async function getVideoDuration(fileOrUrl, { timeoutMs = 8000 } = {}) {
  const needRevoke = typeof fileOrUrl !== 'string';
  const url = needRevoke ? URL.createObjectURL(fileOrUrl) : fileOrUrl;

  try {
    return await new Promise((resolve, reject) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      v.playsInline = true;

      let settled = false;
      const cleanup = () => {
        v.removeEventListener('loadedmetadata', onLoaded);
        v.removeEventListener('error', onError);
        // iOS Safari: libère la ressource
        v.src = '';
        v.load();
      };

      const onLoaded = () => {
        if (settled) return;
        settled = true;
        const d = Number(v.duration);
        cleanup();
        resolve(Number.isFinite(d) ? d : 0);
      };

      const onError = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('Cannot load video metadata'));
      };

      v.addEventListener('loadedmetadata', onLoaded, { once: true });
      v.addEventListener('error', onError, { once: true });
      v.src = url;

      if (timeoutMs) {
        setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error('Metadata load timeout'));
        }, timeoutMs);
      }
    });
  } finally {
    if (needRevoke) URL.revokeObjectURL(url);
  }
}


const toHMS = (s) => {
  const ms = Math.round((s % 1) * 1000);
  s = Math.floor(s);
  const hh = Math.floor(s/3600).toString().padStart(2,'0');
  const mm = Math.floor((s%3600)/60).toString().padStart(2,'0');
  const ss = Math.floor(s%60).toString().padStart(2,'0');
  return `${hh}:${mm}:${ss}.${ms.toString().padStart(3,'0')}`;
};

// ✅ Convertit transcript → phrases (sans couper un mot) + option pour combler les silences
function buildSentenceSegmentsFromTranscript(
  transcript,
  { videoDuration = null, includeSilences = false, maxGap = 0.7 } = {}
) {
  if (!transcript) return [];

  const punctEnd = /[\.!\?…]$/;

  // 1) Récupérer un tableau unifié de mots {start,end,text}
  let words = [];
  if (Array.isArray(transcript.words) && transcript.words.length) {
    words = transcript.words.map(w => ({
      start: Number(w.start), end: Number(w.end),
      text: String(w.word ?? w.text ?? '').trim()
    }));
  } else if (Array.isArray(transcript.segments)) {
    for (const seg of transcript.segments) {
      if (Array.isArray(seg.words) && seg.words.length) {
        words.push(...seg.words.map(w => ({
          start: Number(w.start), end: Number(w.end),
          text: String(w.word ?? w.text ?? '').trim()
        })));
      } else if (typeof seg.text === 'string' && seg.text.trim()) {
        // Fallback approx: découpe le texte du segment par ponctuation et répartit sur la durée
        const pieces = seg.text.split(/(?<=[\.!\?…])\s+/).map(s => s.trim()).filter(Boolean);
        const t0 = Number(seg.start) || 0, t1 = Number(seg.end) || t0;
        const dur = Math.max(0, t1 - t0);
        const totalChars = pieces.reduce((a, s) => a + s.length, 0) || 1;
        let t = t0;
        for (const p of pieces) {
          const slice = dur * (p.length / totalChars);
          const s = t, e = t + slice;
          words.push({ start: s, end: e, text: p }); // traité comme 1 “mot-phrase”
          t = e;
        }
      }
    }
  }

  // 2) Construire les phrases (groupage par ponctuation OU pause > maxGap)
  const out = [];
  let curStart = null, curEnd = null, buf = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    if (!w || !isFinite(w.start) || !isFinite(w.end)) continue;
    const token = (w.text || '').trim();
    if (!token) continue;

    if (curStart == null) curStart = w.start;
    curEnd = w.end;
    buf.push(token);

    const next = words[i + 1];
    const isPunct = punctEnd.test(token);
    const longGap = next ? (Number(next.start) - Number(w.end)) > maxGap : true; // fin de flux = fin de phrase

    if (isPunct || longGap) {
      const label = buf.join(' ').replace(/\s+([,.;:!?…])/g, '$1').trim();
      if (label) out.push({
        start: +curStart.toFixed(3),
        end: +curEnd.toFixed(3),
        label,
        enabled: true
      });
      buf = []; curStart = null; curEnd = null;
    }
  }

  // 3) Option: couvrir 100% du film en ajoutant des segments “silence”
  if (includeSilences && typeof videoDuration === 'number' && isFinite(videoDuration)) {
    out.sort((a,b) => a.start - b.start);
    const full = [];
    let t = 0;
    for (const s of out) {
      if (s.start > t + 0.05) {
        full.push({ start: +t.toFixed(3), end: +s.start.toFixed(3), label: '— silence —', enabled: true, kind: 'silence' });
      }
      full.push(s);
      t = s.end;
    }
    if (videoDuration > t + 0.05) {
      full.push({ start: +t.toFixed(3), end: +videoDuration.toFixed(3), label: '— silence —', enabled: true, kind: 'silence' });
    }
    return full;
  }

  return out;
}




// — Construit une séquence gapless à partir du transcript
function buildGaplessSequence(transcript, videoDurationSec, {
  silenceLabel = 'Silence',
  gapThreshold = 2.0 // >2s = nouveau group
} = {}) {
  const segs = Array.isArray(transcript?.segments) ? transcript.segments : [];
  const items = segs
    .map(s => ({
      start: Math.max(0, parseFloat(s.start ?? s.start_time ?? 0)),
      end: Math.max(0, parseFloat(s.end ?? s.end_time ?? 0)),
      label: s.label || s.text?.slice(0,80) || ''
    }))
    .filter(s => s.end > s.start)
    .sort((a,b) => a.start - b.start);

  const out = [];
  let t = 0, group = 1, lastEnd = 0;

  const pushGap = (a,b) => {
    if (b - a > 0.001) {
      if ((a - lastEnd) > gapThreshold) group++; // gros trou → nouveau chapitre
      out.push({ start: a, end: b, label: silenceLabel, group });
      lastEnd = b;
    }
  };

  for (const s of items) {
    if (s.start > t) pushGap(t, s.start);
    // long silence avant ce segment ? nouveau group
    if ((s.start - lastEnd) > gapThreshold) group++;
    out.push({ start: s.start, end: s.end, label: s.label, group });
    t = s.end; lastEnd = s.end;
  }

  // queue jusqu'à fin vidéo
  const total = Math.max(videoDurationSec || t, t);
  if (total > t) pushGap(t, total);

  // normalise/merge très courts bouts
  return out
    .filter(s => s.end - s.start > 0.05)
    .map(s => ({ ...s, start: +s.start.toFixed(3), end: +s.end.toFixed(3) }));
}

// + utils (au-dessus de handleSuggestionClick)
function extractSubjectsFromTranscript(transcript, topN=8){
  const text = (transcript?.segments||[]).map(s=>s.label||s.text||'').join(' ').toLowerCase();
  const stop = new Set(['le','la','les','un','une','des','de','du','d',"et","ou","mais","donc","or","ni","car",'the','a','an','and','or','but','so','for','to','of','in']);
  const freq = new Map();
  for (const w of text.replace(/[^\w\s-]/g,' ').split(/\s+/)) {
    if (!w || stop.has(w) || w.length<3) continue;
    freq.set(w,(freq.get(w)||0)+1);
  }
  return [...freq.entries()].sort((a,b)=>b[1]-a[1]).slice(0, topN).map(([w])=>w);
}
function toSequenceTextV11(sequence, meta={}, groupsSubjects=new Map()){
  const esc=s=>String(s).replace(/"/g,'\\"'); const L=[];
  L.push('SEQUENCE v1.1');
  if(meta.title) L.push(`TITLE: "${esc(meta.title)}"`);
  if(meta.description) L.push(`DESCRIPTION: "${esc(meta.description)}"`);
  if(Array.isArray(meta.subjects)) L.push(`SUBJECTS: [${meta.subjects.map(s=>`"${esc(s)}"`).join(', ')}]`);
  if(meta.name) L.push(`\nSOURCE: {name:"${esc(meta.name)}"}`);
  if(typeof meta.duration==='number') L.push(`DURATION: ${toHMS(meta.duration)}`);
  L.push('');
  let g=null;
  for(const seg of sequence){
    if(seg.group!==g){
      g=seg.group;
      const subs = groupsSubjects.get(g) || seg.subjects || [];
      L.push(`# group=${g} title="${esc(seg.title||seg.label?.slice(0,40)||'Section')}" subjects=[${subs.map(s=>`"${esc(s)}"`).join(', ')}]`);
    }
    L.push(`SEG start=${toHMS(seg.start)} end=${toHMS(seg.end)} label="${esc(seg.label||'')}" group=${seg.group}`);
  }
  return L.join('\n');
}

// — Sérialise SEQUENCE v1
function toSequenceText(sequence, meta = {}) {
  const lines = [];
  lines.push(`SEQUENCE v1`);
  if (meta.name) lines.push(`SOURCE: {name:"${meta.name.replace(/"/g,'\\"')}"}`);
  if (typeof meta.duration === 'number') lines.push(`DURATION: ${toHMS(meta.duration)}`);
  lines.push('');

  let currentGroup = null;
  for (const seg of sequence) {
    if (seg.group !== currentGroup) {
      currentGroup = seg.group;
      lines.push(`# group=${currentGroup} title="${seg.title || seg.label?.slice(0,40) || 'Section'}"`);
    }
    lines.push(
      `SEG start=${toHMS(seg.start)} end=${toHMS(seg.end)} ` +
      `label="${(seg.label||'').replace(/"/g,'\\"')}" group=${seg.group}`
    );
  }
  return lines.join('\n');
}

export default function AIVideoChat() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content:
        "🎬 Hey 👋 ! Je suis ton Monteur vidéo IA .\n\nUpload une vidéo et demande-moi de l'éditer .",
      timestamp: Date.now() - 5000,
    },
  ]);

  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [mediaId, setMediaId] = useState(() => `chat-${Date.now()}`);
  const [ffmpegReady, setFFmpegReady] = useState(false);
  const [introOutroBlob, setIntroOutroBlob] = useState(null);

  // …états existants…
  const [introOn, setIntroOn] = useState(false);
  const [outroOn, setOutroOn] = useState(false);
  const inputRef = useRef(null);

  const [betaMode, setBetaMode] = useState(false);



  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fileAttachRef = useRef(null);

  const [attachedFiles, setAttachedFiles] = useState([]);
  const inputIslandRef = useRef(null);
  const [bottomPad, setBottomPad] = useState(120);

  const handleAttachPick = (e) => {
  const files = Array.from(e.target.files || []);
  const videos = files.filter(f => f.type.startsWith('video/'));
  if (videos.length) {
    setAttachedFiles(prev => [...prev, ...videos]);
    // on déclenche ton flux existant avec la 1re vidéo
    handleVideoUpload(videos[0]);
  }
  e.target.value = '';
};

const removeAttached = (index) => {
  setAttachedFiles(prev => prev.filter((_, i) => i !== index));
};

  useEffect(() => {
  const el = inputIslandRef.current;
  if (!el) return;
  const ro = new ResizeObserver(() => {
    const h = el.offsetHeight || 0;
    setBottomPad(h + 16); // petit espace
  });
  ro.observe(el);
  return () => ro.disconnect();
}, []);

  useEffect(() => {
  scrollToBottom();
}, [bottomPad]);

  const objectUrlsRef = useRef(new Set());
  const trackURL = (u) => { if (u) objectUrlsRef.current.add(u); return u; };
  
  useEffect(() => () => {
    for (const u of objectUrlsRef.current) { try { URL.revokeObjectURL(u); } catch {} }
    objectUrlsRef.current.clear();
  }, []);
  

     useEffect(() => {
      const initFFmpeg = async () => {
        try {
          setProcessingStep('Chargement FFmpeg WASM...');
          setIsProcessing(true);
          await loadFFmpeg();
          setFFmpegReady(true);
    
          // Pré-charger l'intro/outro une fois (plus fiable que fetch au moment de l’export)
          try {
            const resp = await fetch(INTRO_OUTRO_URL, { mode: 'cors' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            setIntroOutroBlob(blob);
          } catch (e) {
            console.warn('Intro/Outro preload failed:', e);
            // on continue quand même : fallback sur l’URL directe ou color=black au moment de l’export
          }
    
          setIsProcessing(false);
          setProcessingStep('');
    
          const aiMessage = {
            id: Date.now(),
            type: 'ai',
            content: '⚡ Model chargé ! prêt à éditer.',
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, aiMessage]);
        } catch (error) {
          setIsProcessing(false);
          const errorMessage = {
            id: Date.now(),
            type: 'ai',
            content: `❌ Erreur de chargement FFmpeg : ${error.message}`,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        }
      };
      initFFmpeg();
    }, []);


  
  // Auto-scroll
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Transcription côté serveur (pour l'analyse seulement)
  const transcribeVideo = async (file, videoUrl) => {
    setProcessingStep('🎤 Transcription audio...');

    try {
      // Upload pour transcription
      const uploadResp = await fetch(`${DEFAULT_CF_BASE_US}/getUploadUrl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, prefix: 'chat-videos' }),
      }).then((r) => r.json());

      if (!uploadResp.ok) {
        throw new Error(`Upload URL error: ${uploadResp.error}`);
      }

      // Upload file
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadResp.putUrl, true);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProcessingProgress((e.loaded / e.total) * 50); // 50% pour upload
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });

      setProcessingProgress(60);

      // Transcribe
      const transcribeResp = await fetch(`${DEFAULT_CF_BASE_US}/transcribeWithReplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mediaId: mediaId,
          fileUrl: uploadResp.publicUrl || uploadResp.getUrl,
          language: 'fr',
          numSpeakers: null,
          groupSegments: true,
          prompt: 'Vidéo en français, contenu général.',
        }),
      }).then((r) => r.json());

      if (!transcribeResp.ok) {
        throw new Error(`Transcription error: ${transcribeResp.error}`);
      }

      setProcessingProgress(100);

      return {
        transcript: transcribeResp,
        sourceData: uploadResp,
      };
    } catch (error) {
      throw error;
    }
  };

  // Édition vidéo avec FFmpeg WASM
  const editVideoWithFFmpeg = async (instruction, videoFile, transcript) => {
    if (!ffmpegReady) {
      throw new Error('FFmpeg pas encore chargé');
    }

    setProcessingStep("🤖 Génération du plan d'édition...");
    setProcessingProgress(10);

    try {
      // ⬇️ NEW: construire la séquence v1.1 localement
      const transcriptData = await loadTranscriptJSON(); // fiable (R2 > mémoire)
      const videoDur = uploadedVideo?.duration || await getVideoDuration(videoFile);
      const seq = buildGaplessSequence(transcriptData, videoDur);
      const subjects = extractSubjectsFromTranscript(transcriptData, 8);
      const sequenceText = toSequenceTextV11(seq, {
        name: uploadedVideo?.name,
        duration: videoDur,
        title: "Master sequence",
        description: "Séquence intégrale chapitrée avec labels et sujets.",
        subjects
      });

      const endpoint = betaMode
        ? `${DEFAULT_CF_BASE_US}/generateEditPlanDirect`
        : `${DEFAULT_CF_BASE_US}/generateEditPlanDeepseek`;
      
      const planResp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mediaId,
          instruction,
          sequenceText,
          transcriptData,
          videoDurationSec: uploadedVideo?.duration || await getVideoDuration(videoFile)
        }),
      }).then(r => r.json());


      if (!planResp.ok) {
        throw new Error(`Plan generation error: ${planResp.error}`);
      }

      const segments =
        planResp.plan?.segments?.map((seg) => ({
          start: parseTimeToSeconds(seg.start),
          end: parseTimeToSeconds(seg.end),
          label: seg.label,
        })) || [];

      if (!segments.length) {
        throw new Error('Aucun segment valide généré');
      }

      setProcessingProgress(30);
     setProcessingStep("🤖 Working on it...");

      // 2) Préparer FFmpeg
      const inputName = 'input.mp4';
      const outputName = 'output.mp4';

      await ffmpeg.writeFile(inputName, new Uint8Array(await videoFile.arrayBuffer()));
      setProcessingProgress(40);

      // 3) Construire la commande FFmpeg pour les segments
      let filterComplex = '';
      let concatInputs = '';

      segments.forEach((segment, index) => {
        const start = segment.start.toFixed(3);
        const duration = (segment.end - segment.start).toFixed(3);

        filterComplex += `[0:v]trim=start=${start}:duration=${duration},setpts=PTS-STARTPTS[v${index}];`;
        filterComplex += `[0:a]atrim=start=${start}:duration=${duration},asetpts=PTS-STARTPTS[a${index}];`;
        concatInputs += `[v${index}][a${index}]`;
      });

      filterComplex += `${concatInputs}concat=n=${segments.length}:v=1:a=1[outv][outa]`;

        setProcessingStep('🎬 Assemblage des segments...');
      
      await withFfmpegProgress(40, 50, () =>
        ffmpeg.exec([
          '-i', inputName,
          '-filter_complex', filterComplex,
          '-map', '[outv]',
          '-map', '[outa]',
          '-c:v', 'libx264',
          '-preset', 'ultrafast',
          '-crf', '23',
          '-c:a', 'aac',
          '-b:a', '128k',
          '-movflags', '+faststart',
          '-y',
          outputName,
        ])
      );


      setProcessingProgress(95);
      setProcessingStep('📦 Finalisation...');
      
      // 5) Récupérer le résultat
      const data = await ffmpeg.readFile(outputName);
      const outputBlob = new Blob([data.buffer], { type: 'video/mp4' });
      
      // ⬇️ ICI : on track l'URL pour qu'elle soit révoquée au unmount
      const outputUrl = trackURL(URL.createObjectURL(outputBlob));
      
      setProcessingProgress(100);
      
      // Nettoyage
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);
      
      return {
        videoUrl: outputUrl,
        plan: planResp.plan,
        segments,
        size: outputBlob.size,
      };
    } catch (error) {
      throw error;
    }
  };

const HARD_SUGGESTIONS = [
  {
    label: 'Couper début/fin inutiles',
    instruction: "Supprimer l'introduction et la conclusion, garder seulement le contenu principal",
    hard: true
  },
  { label: 'résumé Moment viral → short ←', instruction: 'reflechi et Choisis un moment au fort potentiel viral pour en faire une courte vidéo destinée aux réseaux sociaux.', hard: true },
  { label: 'Ajouter intro', instruction: '__TOGGLE_INTRO__' },
  { label: 'Ajouter outro', instruction: '__TOGGLE_OUTRO__' },
];

  const getSuggestionClass = (s) => {
  // toggles verts quand actifs
  if (s.instruction === '__TOGGLE_INTRO__') {
    return introOn
      ? 'bg-emerald-500 text-black border-emerald-600'
      : 'bg-zinc-800 text-white/90 border-white/10 hover:bg-zinc-700/80';
  }
  if (s.instruction === '__TOGGLE_OUTRO__') {
    return outroOn
      ? 'bg-emerald-500 text-black border-emerald-600'
      : 'bg-zinc-800 text-white/90 border-white/10 hover:bg-zinc-700/80';
  }
  // hard en violet
  if (s.hard) {
    return 'bg-violet-600/15 text-violet-200 border-violet-400/30 hover:bg-violet-600/25';
  }
  // défaut neutre
  return 'bg-zinc-800 text-white/90 border-white/10 hover:bg-zinc-700/80';
};

const getSuggestionLabel = (s) => {
  if (s.instruction === '__TOGGLE_INTRO__') return introOn ? 'Ajouter intro (ON)' : 'Ajouter intro';
  if (s.instruction === '__TOGGLE_OUTRO__') return outroOn ? 'Ajouter outro (ON)' : 'Ajouter outro';
  return s.label;
};



  const handleVideoUpload = async (file) => {
  if (!file) return;

const videoUrl = trackURL(URL.createObjectURL(file));
  const videoDuration = await getVideoDuration(file);


  const userMessage = {
    id: Date.now(),
    type: 'user',
    content: `📹 J'ai uploadé : ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`,
    videoUrl,
    timestamp: Date.now(),
  };

  setMessages((prev) => [...prev, userMessage]);
  setIsProcessing(true);
  setProcessingProgress(0);

  try {
    // 1) Extraction audio
    setProcessingStep('Extraction data…');
    const { blob, url: localAudioUrl, fmt } = await extractAudio(file, 'mp3');
    trackURL(localAudioUrl); // ✅ évite la fuite, sera révoquée au unmount

    setProcessingProgress(40);


    // 2) Upload vers R2
    setProcessingStep('Upload …');
    const { publicUrl: audioPublicUrl, getUrl: audioGetUrl } = await uploadAudioToR2(blob, fmt);
    setProcessingProgress(90);

    let sugg;
    
    /*
    // 3) Transcription Gemini (➡️ passe l’URL SIGNÉE)
    setProcessingStep("🤖 Transcription avec Gemini…");
    const resp = await fetch(`${DEFAULT_CF_BASE_US}/transcribeWithGemini`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audioUrl: audioGetUrl, mediaId }), // <- IMPORTANT
    });
    const data = await resp.json();
    if (!data.ok) throw new Error(data.error || "Gemini transcription failed");


       setProcessingStep("🧠 Génération des suggestions…");
    // 4) Suggestions Gemini (inchangé)
    const suggResp = await fetch(`${DEFAULT_CF_BASE_US}/suggestEditsWithGemini`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mediaId, transcriptText: data.transcript })
    });
    const sugg = await suggResp.json();

    */

     // 3) Transcription Replicate Whisper
   // 3) Transcription Replicate Whisper
    setProcessingStep("Transcription...");
    const resp = await fetch(`${DEFAULT_CF_BASE_US}/QuicktranscribeWithReplicate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mediaId,
        fileUrl: audioGetUrl,
        language: "fr"
      }),
    });
    
    const data = await resp.json();
    if (!data.ok) throw new Error(data.error || "Replicate transcription failed");

    const { transcriptSrtUrl, transcriptJsonUrl, transcriptTxtUrl } = data || {};

    
    // Convertir data.transcript en string si nécessaire
    const transcriptText = typeof data.transcript === 'string' 
      ? data.transcript 
      : JSON.stringify(data.transcript);

    /*
    setProcessingStep("🧠 Génération des suggestions…");
    const suggResponse = await fetch(`${DEFAULT_CF_BASE_US}/suggestEditsWithGemini`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mediaId, transcriptText }) // Utiliser transcriptText au lieu de data.transcript
    });
    sugg = await suggResponse.json();
   //const sugg = await suggResponse.json();
    */

    /*

    setProcessingStep("🧠 Génération des suggestions (ChatGPT)…");
      const suggResponse = await fetch(`${DEFAULT_CF_BASE_US}/suggestEditsWithChatGPT`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mediaId, transcriptText })
      });
      sugg = await suggResponse.json();
      */

       setProcessingStep("🧠 Génération des suggestions...");
      const suggResponse = await fetch(`${DEFAULT_CF_BASE_US}/suggestEditsWithDeepseek`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mediaId, transcriptText })
      });
      sugg = await suggResponse.json();





 


    
    // 5) Stocker infos
    setUploadedVideo({
      file,
      url: videoUrl,
      name: file.name,
      duration: videoDuration,
      audioUrl: audioPublicUrl || audioGetUrl,
      localAudioPreview: localAudioUrl,
      audioFormat: fmt,
      transcribed: true,
      transcript: data.transcript,
      transcriptUrls: {
        srt: transcriptSrtUrl || null,
        json: transcriptJsonUrl || null,
        txt: transcriptTxtUrl || null,
      },
    });
        
    // 6) Message chat (lien pour l’UI)
    const linkForUI = audioPublicUrl || audioGetUrl;
    const aiMessage = {
      id: Date.now() + 1,
      type: 'ai',
      content:
       // `✅ Vidéo prête • audio extrait en **${fmt.toUpperCase()}**\n\n` +
      //  `☁️ Audio sauvegardé dans R2 : ${linkForUI}\n\n` +
        (sugg.ok
          ? ` ${sugg.summary}\n\n💡 Suggestions :`
          : '📊 Analyse terminée.'),
      //audioUrl: localAudioUrl,
     // suggestions: sugg.ok ? sugg.suggestions : [],
       suggestions: [
          ...HARD_SUGGESTIONS
        // ...(sugg.ok ? (sugg.suggestions || []) : [])
        
       ],
     // suggestions: sugg.ok ? sugg.suggestions : [],
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, aiMessage]);

    setProcessingProgress(100);
  } catch (error) {
    console.error(error);
    const errorMessage = {
      id: Date.now() + 1,
      type: 'ai',
      content: `❌ Erreur extraction/upload audio : ${error.message}`,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, errorMessage]);
  } finally {
    setIsProcessing(false);
    setProcessingStep('');
    setProcessingProgress(0);
  }
};

const pushPreviewWithIntroOutro = async (which /* 'intro' | 'outro' */) => {
  const duration = uploadedVideo?.duration || await getVideoDuration(uploadedVideo.file);
  const segments = [{ start: 0, end: Math.max(0, duration), label: 'Vidéo complète' }];

  setMessages(prev => [...prev, {
    id: Date.now(),
    type: 'ai',
    content: `🎬 **Aperçu prêt** • ${which === 'intro' ? 'Intro activée' : 'Outro activée'}\n\nTu peux exporter ou ajuster avant.`,
    timestamp: Date.now(),
    videoPreview: {
      file: uploadedVideo.file,
      segments,
      plan: {
        title: which === 'intro' ? 'Ajouter une intro' : 'Ajouter une outro',
        segments: [{ start: '00:00:00.000', end: toHMS(duration), label: 'Full' }],
      },
      instruction: which === 'intro' ? 'Ajouter intro' : 'Ajouter outro',
      // 👇 Ces deux flags seront lus par SimpleVideoPreview
      defaultAddIntro: introOn,   // ✅ injecte le toggle
        defaultAddOutro: outroOn    // ✅ injecte le toggle
    }
  }]);
};

  
  // Traitement des instructions d'édition
  const handleEditInstruction = async (instruction) => {
    if (!uploadedVideo) {
      const aiMessage = {
        id: Date.now(),
        type: 'ai',
        content: "🎬 Upload d'abord une vidéo pour que je puisse l'éditer !",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiMessage]);
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);

    try {
        const result = await editVideoWithFFmpeg(
          instruction,
          uploadedVideo.file,
          uploadedVideo.transcript,
          uploadedVideo.transcriptUrls // ⬅️ NEW
        );

      // Message de succès avec vidéo éditée
      const aiMessage = {
        id: Date.now(),
        type: 'ai',
        content: `🎬 **Vidéo créée !** ⚡\n\n📝 **Ta demande** : "${instruction}"\n\n✨ **Résultat** :\n• ${result.segments?.length || 0} segments assemblés\n• Taille : ${(result.size / 1024 / 1024).toFixed(1)} MB\n• Édité instantanément avec FFmpeg WASM\n\n${result.plan?.title ? `🎯 **Titre suggéré** : ${result.plan.title}` : ''}`,
        videoUrl: result.videoUrl,
        timestamp: Date.now(),
        editPlan: result.plan,
        isLocalFile: true,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now(),
        type: 'ai',
        content: `❌ Erreur d'édition : ${error.message}\n\nPeux-tu réessayer avec une instruction plus simple ?`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
      setProcessingProgress(0);
    }
  };

  // Envoi de message
  const handleSendMessage = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = input;
    setInput('');

    if (uploadedVideo) {
      await handleSuggestionClick(currentInput);
    } else {
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content:
          "🎥 Pour commencer, upload une vidéo ! Je vais l'analyser puis l'éditer selon tes instructions.\n\n⚡ Tout se fait dans ton navigateur grâce à FFmpeg WASM - c'est rapide et privé !",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    }
  };

  // Helper to format a duration in seconds as "m:ss"
const fmtTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};


  // Parse time to seconds
  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':').map((p) => parseFloat(p));
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    return parseFloat(timeStr) || 0;
  };

  // 🔹 Charge le transcript JSON fiable (priorité à l'URL R2), fallback sur l'objet en mémoire
const loadTranscriptJSON = async () => {
  const url = uploadedVideo?.transcriptUrls?.json;
  if (url) {
    try {
      const r = await fetch(url, { cache: 'no-store' });
      if (r.ok) return await r.json();
      console.warn('Transcript JSON fetch failed with status:', r.status);
    } catch (e) {
      console.warn('Transcript JSON fetch error:', e);
    }
  }
  const t = uploadedVideo?.transcript;
  if (!t) return null;
  if (typeof t === 'object') return t;
  try { return JSON.parse(t); } catch { return null; }
};


  // Download video
  const downloadVideo = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'edited-video.mp4';
    a.click();
  };

  
/*
const handleSuggestionClick = async (instruction) => {
  if (!uploadedVideo) {
    setMessages((prev) => [...prev, {
      id: Date.now(),
      type: 'ai',
      content: "🎬 Upload d'abord une vidéo pour que je puisse l'éditer !",
      timestamp: Date.now(),
    }]);
    return;
  }

   // 🔸 Gestion immédiate des toggles Intro/Outro
 if (instruction === '__TOGGLE_INTRO__') {
   await pushPreviewWithIntroOutro('intro');
   return;
 }
 if (instruction === '__TOGGLE_OUTRO__') {
   await pushPreviewWithIntroOutro('outro');
   return;
 }

  setIsProcessing(true);
  setProcessingProgress(0);
  setProcessingStep("🤖 Génération du plan d'édition...");

  try {
    // ✅ Utilise le JSON du transcript (R2 en priorité)
    const transcriptData = await loadTranscriptJSON();
    if (!transcriptData) throw new Error("Transcript JSON introuvable");
    
    // 1) Construire la séquence texte gapless
    const duration = uploadedVideo?.duration || await getVideoDuration(uploadedVideo.file);
    const fullSequence = buildGaplessSequence(transcriptData, duration);
     const subjects = extractSubjectsFromTranscript(transcriptData, 8);
     const sequenceText = toSequenceTextV11(fullSequence, {
       name: uploadedVideo?.name,
       duration,
       title: "Master sequence",
       description: "Séquence intégrale chapitrée avec labels et sujets.",
       subjects
     });
        
      const endpoint = betaMode
        ? `${DEFAULT_CF_BASE_US}/generateEditPlanGemini`
        : `${DEFAULT_CF_BASE_US}/generateEditPlanDeepseek`;
      
      const planResp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mediaId,
          instruction,
          sequenceText,
          transcriptData,
          videoDurationSec: uploadedVideo?.duration || await getVideoDuration(videoFile)
        }),
      }).then(r => r.json());

    
    if (!planResp.ok) throw new Error(`Plan generation error: ${planResp.error}`);
    
    // 4) Tu gardes ton mapping segments -> preview (inchangé)
    const segments = (planResp.plan?.segments || []).map((seg) => ({
      start: parseTimeToSeconds(seg.start),
      end: parseTimeToSeconds(seg.end),
      label: seg.label,
    }));

    
    if (!segments.length) throw new Error('Aucun segment valide généré');

    const totalDuration = segments.reduce((acc, s) => acc + (s.end - s.start), 0);
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    setMessages((prev) => [...prev, {
      id: Date.now(),
      type: 'ai',
      content:
        `🎬 **Voici ton premier montage !**\n\n` +
        `📝 **Ta demande** : "${instruction}"\n\n` +
        `✨ **Aperçu** :\n• ${segments.length} segments sélectionnés\n• Durée totale : ${formatTime(totalDuration)}\n\n` +
        (planResp.plan?.title ? `🎯 **Titre** : ${planResp.plan.title}\n\n` : '') +
        `👇 **Prévisualise et exporte quand tu es satisfait**`,
      timestamp: Date.now(),
        videoPreview: {
          file: uploadedVideo.file,
          segments,
          plan: planResp.plan,
          instruction,
          defaultAddIntro: introOn,   // ✅ passe l’état au preview
          defaultAddOutro: outroOn    // ✅ idem
        }

    }]);

  } catch (error) {
    setMessages((prev) => [...prev, {
      id: Date.now(),
      type: 'ai',
      content: `❌ Erreur lors de la génération du plan : ${error.message}`,
      timestamp: Date.now(),
    }]);
  } finally {
    setIsProcessing(false);
    setProcessingStep('');
    setProcessingProgress(0);
  }
};
*/
  // Remplacez la fonction handleSuggestionClick dans AIVideoChat.jsx

const handleSuggestionClick = async (instruction) => {
  if (!uploadedVideo) {
    setMessages((prev) => [...prev, {
      id: Date.now(),
      type: 'ai',
      content: "🎬 Upload d'abord une vidéo pour que je puisse l'éditer !",
      timestamp: Date.now(),
    }]);
    return;
  }

   // 🔸 Gestion immédiate des toggles Intro/Outro
 if (instruction === '__TOGGLE_INTRO__') {
   await pushPreviewWithIntroOutro('intro');
   return;
 }
 if (instruction === '__TOGGLE_OUTRO__') {
   await pushPreviewWithIntroOutro('outro');
   return;
 }

  setIsProcessing(true);
  setProcessingProgress(0);
  setProcessingStep("🤖 Génération du plan d'édition...");

  const MAX_RETRIES = 5;
  let attempt = 0;
  let lastError = null;

  while (attempt < MAX_RETRIES) {
    try {
      attempt++;
      
      if (attempt > 1) {
        setProcessingStep(`🤖 Tentative ${attempt}/${MAX_RETRIES} - Génération du plan...`);
        // Petite pause entre les tentatives
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // ✅ Utilise le JSON du transcript (R2 en priorité)
      const transcriptData = await loadTranscriptJSON();
      if (!transcriptData) throw new Error("Transcript JSON introuvable");
      
      // 1) Construire la séquence texte gapless
      const duration = uploadedVideo?.duration || await getVideoDuration(uploadedVideo.file);
      const fullSequence = buildGaplessSequence(transcriptData, duration);
      const subjects = extractSubjectsFromTranscript(transcriptData, 8);
      const sequenceText = toSequenceTextV11(fullSequence, {
        name: uploadedVideo?.name,
        duration,
        title: "Master sequence",
        description: "Séquence intégrale chapitrée avec labels et sujets.",
        subjects
      });
          
      const endpoint = betaMode
        ? `${DEFAULT_CF_BASE_US}/generateEditPlanGemini`
        : `${DEFAULT_CF_BASE_US}/generateEditPlanDeepseek`;
      
      const planResp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mediaId,
          instruction,
          sequenceText,
          transcriptData,
          // ✅ FIX: Utiliser uploadedVideo.file au lieu de videoFile non défini
          videoDurationSec: uploadedVideo?.duration || await getVideoDuration(uploadedVideo.file)
        }),
      }).then(r => r.json());
      
      if (!planResp.ok) throw new Error(`Plan generation error: ${planResp.error}`);
      
      // 4) Tu gardes ton mapping segments -> preview (inchangé)
      const segments = (planResp.plan?.segments || []).map((seg) => ({
        start: parseTimeToSeconds(seg.start),
        end: parseTimeToSeconds(seg.end),
        label: seg.label,
      }));

      if (!segments.length) {
        throw new Error('No valid segments generated');
      }

      // ✅ Si on arrive ici, c'est un succès !
      const totalDuration = segments.reduce((acc, s) => acc + (s.end - s.start), 0);
      const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      };

      const successMessage = attempt > 1 
        ? `🎬 **Plan généré avec succès** (tentative ${attempt}/${MAX_RETRIES})\n\n`
        : `🎬 **Voici ton premier montage !**\n\n`;

      setMessages((prev) => [...prev, {
        id: Date.now(),
        type: 'ai',
        content:
          successMessage +
          `📝 **Ta demande** : "${instruction}"\n\n` +
          `✨ **Aperçu** :\n• ${segments.length} segments sélectionnés\n• Durée totale : ${formatTime(totalDuration)}\n\n` +
          (planResp.plan?.title ? `🎯 **Titre** : ${planResp.plan.title}\n\n` : '') +
          `👇 **Prévisualise et exporte quand tu es satisfait**`,
        timestamp: Date.now(),
        videoPreview: {
          file: uploadedVideo.file,
          segments,
          plan: planResp.plan,
          instruction,
          defaultAddIntro: introOn,
          defaultAddOutro: outroOn
        }
      }]);

      // Succès, on sort de la boucle
      break;

    } catch (error) {
      lastError = error;
      console.warn(`Attempt ${attempt}/${MAX_RETRIES} failed:`, error.message);
      
      // Si c'est la dernière tentative, on affiche l'erreur
      if (attempt === MAX_RETRIES) {
        const errorMessage = error.message.includes('No valid segments generated')
          ? `❌ Impossible de générer un plan d'édition après ${MAX_RETRIES} tentatives.\n\nL'IA n'arrive pas à identifier des segments pertinents pour : "${instruction}"\n\n💡 **Suggestions :**\n• Essaie une instruction plus spécifique\n• Vérifie que ta vidéo contient du contenu parlé\n• Reformule ta demande différemment`
          : `❌ Erreur lors de la génération du plan : ${error.message}`;

        setMessages((prev) => [...prev, {
          id: Date.now(),
          type: 'ai',
          content: errorMessage,
          timestamp: Date.now(),
        }]);
      }
      
      // Si ce n'est pas une erreur de "No valid segments", on arrête les tentatives
      if (!error.message.includes('No valid segments generated') && !error.message.includes('Plan generation error')) {
        setMessages((prev) => [...prev, {
          id: Date.now(),
          type: 'ai',
          content: `❌ Erreur lors de la génération du plan : ${error.message}`,
          timestamp: Date.now(),
        }]);
        break;
      }
    }
  }

  setIsProcessing(false);
  setProcessingStep('');
  setProcessingProgress(0);
};

  // Wrappe un ffmpeg.exec avec une progression bornée (start% → start+span%)
async function withFfmpegProgress(startPct, spanPct, runExec) {
  // branche un handler one-shot
  ffmpeg.on('progress', ({ progress = 0 }) => {
    const p = Math.min(99, Math.round(startPct + progress * spanPct));
    setProcessingProgress(p);
  });

  try {
    return await runExec();
  } finally {
    // débranche pour éviter d'empiler les handlers aux appels suivants
    ffmpeg.on('progress', () => {});
  }
}

  // Face centering (BlazeFace): calcule (fx, fy) pour chaque segment
// Face centering (BlazeFace): calcule (fx, fy) pour chaque segment
async function computeFaceCenters(
  videoFile,
  segments,
  {
    samplesPerSegment = 8,   // nb d'images analysées par segment
    minConfidence = 0.75,    // seuil de détection
  } = {}
) {
  // 🔸 Imports dynamiques pour éviter d'alourdir le bundle initial
  const [blazeMod, tf] = await Promise.all([
    import('@tensorflow-models/blazeface'), // expose .load()
    import('@tensorflow/tfjs-core'),
  ]);

  // requis par les models TFJS
  await import('@tensorflow/tfjs-converter');

  // backend GPU si dispo, sinon CPU (important sur certaines machines / navigateurs)
  try {
    await import('@tensorflow/tfjs-backend-webgl');
    await tf.setBackend('webgl');
  } catch {
    await import('@tensorflow/tfjs-backend-cpu');
    await tf.setBackend('cpu');
  }
  await tf.ready();

  const model = await blazeMod.load();

  // ——— Prépare une <video> & un canvas pour les captures ———
  const url = typeof videoFile === 'string' ? videoFile : URL.createObjectURL(videoFile);
  const v = document.createElement('video');
  v.src = url;
  v.crossOrigin = 'anonymous';
  v.muted = true;
  v.playsInline = true;

  await new Promise((res, rej) => {
    v.onloadedmetadata = () => res();
    v.onerror = () => rej(new Error('Video metadata load failed'));
  });

  const cw = Math.min(640, v.videoWidth || 640);
  const ch = Math.round(cw * (v.videoHeight / (v.videoWidth || cw)));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');

  const seekTo = (t) =>
    new Promise((res) => {
      const onSeeked = () => {
        v.removeEventListener('seeked', onSeeked);
        res();
      };
      v.addEventListener('seeked', onSeeked);
      v.currentTime = Math.max(0, Math.min(v.duration || t + 0.01, t + 0.001));
    });

  const results = [];

  for (const seg of segments) {
    const start = Number(seg.start) || 0;
    const end   = Number(seg.end)   || 0;
    const dur   = Math.max(0, end - start);
    if (dur <= 0) { results.push({ fx: 0.5, fy: 0.5 }); continue; }

    const K = Math.max(1, Math.min(samplesPerSegment, Math.ceil(dur * 4))); // ~4 fps
    const xs = [], ys = [];

    for (let k = 0; k < K; k++) {
      const t = start + ((k + 0.5) / K) * dur;
      await seekTo(t);
      ctx.drawImage(v, 0, 0, cw, ch);

      const preds = await model.estimateFaces(canvas, false);

      // Choisir le visage principal (plus grande aire)
      let best = null, bestArea = -1;
      for (const p of preds) {
        if (p.probability && p.probability[0] < minConfidence) continue;
        const [x1, y1] = p.topLeft;
        const [x2, y2] = p.bottomRight;
        const area = (x2 - x1) * (y2 - y1);
        if (area > bestArea) { bestArea = area; best = { x1, y1, x2, y2 }; }
      }

      if (best) {
        const cx = (best.x1 + best.x2) / 2;
        const cy = (best.y1 + best.y2) / 2;
        xs.push(cx / cw);
        ys.push(cy / ch);
      }
    }

    const median = (arr) => {
      if (!arr.length) return 0.5;
      const a = [...arr].sort((a,b) => a-b);
      const mid = Math.floor(a.length/2);
      return a.length % 2 ? a[mid] : (a[mid-1] + a[mid]) / 2;
    };

    results.push({
      fx: Math.max(0.0, Math.min(1.0, median(xs) || 0.5)),
      fy: Math.max(0.0, Math.min(1.0, median(ys) || 0.5)),
    });
  }

  if (typeof videoFile !== 'string') URL.revokeObjectURL(url);
  return results; // array de { fx, fy } aligné sur segments[]
}


/// Résolutions cible (garde les tiennes)
const DIMENSIONS_BY_ASPECT = {
  '16:9': { w: 1280, h: 720 },
  '1:1' : { w: 1080, h: 1080 },
  '9:16': { w: 720,  h: 1280 },
};

  const INTRO_OUTRO_DURATION = 3;      // tu l'as déjà — garde la valeur que tu veux
const AUDIO_RATE = 44100;  

async function getVideoDims(fileOrUrl) {
  const url = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);
  try {
    const v = document.createElement('video');
    v.src = url; v.muted = true; v.playsInline = true;
    await new Promise((res, rej) => { v.onloadedmetadata = res; v.onerror = () => rej(new Error('meta')); });
    return { w: v.videoWidth || 1920, h: v.videoHeight || 1080 };
  } finally {
    if (typeof fileOrUrl !== 'string') URL.revokeObjectURL(url);
  }
}
/*
// ⚠️ Ajoute une fois, juste après: ffmpeg = new FFmpeg();
const loadFFmpeg = async () => {
  if (ffmpegLoaded) return ffmpeg;

  try {
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');

    ffmpeg = new FFmpeg();

    // ✅ logger ici, maintenant ffmpeg est instancié
    ffmpeg.on('log', ({ type, message }) => {
      if (type === 'error') console.error('[ffmpeg]', message);
    });

    const tryLoad = async (useMT) => {
      const baseURL = useMT
        ? 'https://cdn.jsdelivr.net/npm/@ffmpeg/core-mt@0.12.10/dist/umd'
        : 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';

      const options = {
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      };
      if (useMT) {
        options.workerURL = await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript');
      }
      await ffmpeg.load(options);
    };

    try {
      if (self.crossOriginIsolated) {
        await tryLoad(true);
      } else {
        console.warn('[FFmpeg] crossOriginIsolated=false → using single-thread core');
        await tryLoad(false);
      }
    } catch (e) {
      console.warn('[FFmpeg] core-mt load failed, falling back to single-thread:', e);
      await tryLoad(false);
    }

    ffmpegLoaded = true;
    return ffmpeg;
  } catch (error) {
    console.error('FFmpeg load error:', error);
    throw new Error('Impossible de charger FFmpeg WASM');
  }
};
*/
// --- helper: normalise les variantes de fit mode ---
function normalizeFitMode(mode, fallback = 'contain') {
  const m = String(mode || '').toLowerCase();
  if (['contain','fit','letterbox'].includes(m)) return 'contain';
  if (['cover','crop','fill','zoom'].includes(m)) return 'cover';
  return fallback;
}

// --- Export final (corrigé) ---
// --- Export en 2 passes : master + reframe (crop/fit) ---
// --- Export robuste : master (copy) + reframe (crop/fit) avec garde-fous ---
// --- Export robust : master (copy) + reframe (crop/fit) ---
const handleFinalExport = async (
  videoPreviewData,
  viewport = {
    aspect: '16:9',
    fitMode: 'cover',
    addIntro: false,
    addOutro: false,
    introUrl: '',
    outroUrl: ''
  }
) => {
  setIsProcessing(true);
  setProcessingProgress(0);

  const uniq = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const DIM = { '16:9': { w: 1280, h: 720 }, '1:1': { w: 1080, h: 1080 }, '9:16': { w: 720, h: 1280 } };
  const { aspect = '16:9', fitMode = 'cover', addIntro, addOutro, introUrl, outroUrl } = viewport || {};
  const { w: TW, h: TH } = DIM[aspect] || DIM['16:9'];

  // Helper for ffmpeg progress
  const withProgress = async (start, span, fn) => {
    if (ffmpeg?.on) {
      ffmpeg.on('progress', ({ progress = 0 }) => {
        const p = Math.min(99, Math.round(start + progress * span));
        setProcessingProgress(p);
      });
    }
    try { return await fn(); }
    finally { if (ffmpeg?.on) ffmpeg.on('progress', () => {}); }
  };

  try {
    if (!ffmpegReady) throw new Error('FFmpeg pas encore chargé');

    // Validate segments
    const segments = (videoPreviewData?.segments || [])
      .map(s => ({
        start: +Number(s.start || 0).toFixed(3),
        end: +Number(s.end || 0).toFixed(3)
      }))
      .filter(s => s.end > s.start + 0.01);
    if (!segments.length) throw new Error('Aucun segment à exporter');

    // 1) Write input file
    const inName = `in_${uniq}.mp4`;
    await ffmpeg.writeFile(inName, new Uint8Array(await videoPreviewData.file.arrayBuffer()));

    // 2) Create master file from segments (copy)
    const masterName = `master_${uniq}.mp4`;
    if (segments.length === 1) {
      const { start, end } = segments[0];
      const dur = end - start;
      await withProgress(0, 25, () => ffmpeg.exec([
        '-ss', start.toFixed(3),
        '-i', inName,
        '-t', dur.toFixed(3),
        '-c', 'copy',
        '-avoid_negative_ts', 'make_zero',
        '-fflags', '+genpts',
        '-movflags', '+faststart',
        '-y', masterName,
      ]));
    } else {
      for (let i = 0; i < segments.length; i++) {
        const { start, end } = segments[i];
        const dur = end - start;
        await ffmpeg.exec([
          '-ss', start.toFixed(3),
          '-i', inName,
          '-t', dur.toFixed(3),
          '-c', 'copy',
          '-avoid_negative_ts', 'make_zero',
          '-y', `seg_${uniq}_${i}.mp4`,
        ]);
        setProcessingProgress(5 + Math.round(((i + 1) / segments.length) * 15));
      }
      await ffmpeg.writeFile(`list_${uniq}.txt`,
        new TextEncoder().encode(segments.map((_, i) => `file 'seg_${uniq}_${i}.mp4'`).join('\n'))
      );
      await withProgress(20, 5, () => ffmpeg.exec([
        '-f', 'concat', '-safe', '0',
        '-i', `list_${uniq}.txt`,
        '-fflags', '+genpts',
        '-c', 'copy',
        '-movflags', '+faststart',
        '-y', masterName,
      ]));
    }

    // 3) Define the reframe filter (crop vs contain)
    const vf = (fitMode === 'cover')
      ? `scale=${TW}:${TH}:force_original_aspect_ratio=increase,crop=${TW}:${TH},fps=30,format=yuv420p`
      : `scale=${TW}:${TH}:force_original_aspect_ratio=decrease,pad=${TW}:${TH}:(ow-iw)/2:(oh-ih)/2,fps=30,format=yuv420p`;

    // 4) Reframe the master file
    setProcessingStep('Reformatage du montage…');
    const mainRefName = `main_ref_${uniq}.mp4`;
    let ok = false;
    try {
      await withProgress(25, 35, () => ffmpeg.exec([
        '-i', masterName,
        '-vf', vf,
        '-map', '0:v:0',
        '-map', '0:a:0?',
        '-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '23',
        '-c:a', 'aac', '-b:a', '160k', '-ar', '44100', '-ac', '2',
        '-movflags', '+faststart',
        '-y', mainRefName,
      ]));
      ok = true;
    } catch (err) {
      console.warn('Reframe montage A/V échoué, tentative vidéo seule');
    }
    if (!ok) {
      await withProgress(25, 35, () => ffmpeg.exec([
        '-i', masterName,
        '-vf', vf,
        '-map', '0:v:0',
        '-an',
        '-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '23',
        '-pix_fmt', 'yuv420p',
        '-movflags', '+faststart',
        '-y', mainRefName,
      ]));
    }

    // Helper to load and reframe intro/outro clip
    const reframeClip = async (clipBlob, clipUrl, suffix) => {
      const srcName = `${suffix}_src_${uniq}.mp4`;
      let wrote = false;
      if (!wrote && clipBlob) {
        try {
          await ffmpeg.writeFile(srcName, new Uint8Array(await clipBlob.arrayBuffer()));
          wrote = true;
        } catch {}
      }
      if (!wrote && clipUrl) {
        try {
          const resp = await fetch(clipUrl, { mode: 'cors' });
          if (!resp.ok) throw new Error();
          const blob = await resp.blob();
          await ffmpeg.writeFile(srcName, new Uint8Array(await blob.arrayBuffer()));
          wrote = true;
        } catch {}
      }
      if (!wrote) {
      // fallback: black clip with silent audio
        try {
          await ffmpeg.exec([
            '-f','lavfi','-i',`color=c=black:s=${TW}x${TH}:r=30`,
            '-f','lavfi','-i','anullsrc=r=44100:cl=stereo',
            '-shortest','-t', '3',  // 3 seconds default length
            '-c:v','libx264','-preset','ultrafast','-crf','24','-pix_fmt','yuv420p',
            '-c:a','aac','-b:a','128k',
            '-movflags','+faststart',
            '-y', srcName,
          ]);
          wrote = true;
        } catch {}
      }
      const outName = `${suffix}_${uniq}.mp4`;
      // reframe clip
      try {
        await ffmpeg.exec([
          '-i', srcName,
          '-vf', vf,
          '-map', '0:v:0',
          '-map', '0:a:0?',
          '-c:v', 'libx264', '-preset','ultrafast','-crf','24',
          '-c:a', 'aac', '-b:a','160k','-ar','44100','-ac','2',
          '-movflags','+faststart',
          '-y', outName,
        ]);
      } catch {
        await ffmpeg.exec([
          '-i', srcName,
          '-vf', vf,
          '-map', '0:v:0',
          '-an',
          '-c:v', 'libx264','-preset','ultrafast','-crf','24',
          '-pix_fmt','yuv420p',
          '-movflags','+faststart',
          '-y', outName,
        ]);
      }
      // cleanup src
      try { await ffmpeg.deleteFile(srcName); } catch {}
      return outName;
    };

    // 5) If intro/outro requested, reframe and concatenate
    let finalName = mainRefName;
    if (addIntro || addOutro) {
      const parts = [];
      if (addIntro) parts.push(await reframeClip(introOutroBlob, introUrl, 'intro'));
      parts.push(mainRefName);
      if (addOutro) parts.push(await reframeClip(introOutroBlob, outroUrl, 'outro'));

      const concatList = parts.map(f => `file '${f}'`).join('\n');
      const concatFile = `concat_${uniq}.txt`;
      await ffmpeg.writeFile(concatFile, new TextEncoder().encode(concatList));
      const mergedName = `final_${uniq}.mp4`;
      await ffmpeg.exec([
        '-f','concat','-safe','0',
        '-i', concatFile,
        '-c','copy',
        '-movflags','+faststart',
        '-y', mergedName
      ]);
      finalName = mergedName;

      // cleanup intermediate intro/outro files
      for (const f of parts) {
        if (f !== mainRefName) {
          try { await ffmpeg.deleteFile(f); } catch {}
        }
      }
      try { await ffmpeg.deleteFile(concatFile); } catch {}
    }

    // 6) Read final output, clean up, and send message
    const data = await ffmpeg.readFile(finalName);
    const blob = new Blob([data.buffer], { type: 'video/mp4' });
    const url = trackURL(URL.createObjectURL(blob));

    try { await ffmpeg.deleteFile(inName); } catch {}
    try { await ffmpeg.deleteFile(masterName); } catch {}
    try { await ffmpeg.deleteFile(mainRefName); } catch {}
    try { await ffmpeg.deleteFile(finalName); } catch {}
    for (let i = 0; i < segments.length; i++) {
      try { await ffmpeg.deleteFile(`seg_${uniq}_${i}.mp4`); } catch {}
    }

    const totalDuration = segments.reduce((acc, s) => acc + (s.end - s.start), 0)
      + (addIntro ? INTRO_OUTRO_DURATION : 0)
      + (addOutro ? INTRO_OUTRO_DURATION : 0);

    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'ai',
      content:
        `✅ **Export terminé** (${aspect} • ${fitMode === 'cover' ? 'Crop' : 'Letterbox'})` +
        `${addIntro ? ' • +Intro' : ''}${addOutro ? ' • +Outro' : ''}\n` +
        `• ${segments.length} segment${segments.length > 1 ? 's' : ''}\n` +
        `• Durée : ${fmtTime(totalDuration)}\n` +
        `• Résolution : ${TW}×${TH}`,
      videoUrl: url,
      timestamp: Date.now(),
      plan: videoPreviewData.plan,
      isLocalFile: true,
    }]);
  } catch (err) {
    console.error('Export error:', err);
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'ai',
      content: `❌ Erreur export : ${err?.message || String(err)}`,
      timestamp: Date.now(),
    }]);
  } finally {
    setIsProcessing(false);
    setProcessingProgress(0);
    setProcessingStep('');
    if (ffmpeg?.on) ffmpeg.on('progress', () => {});
  }
};





// état visuel du drag
const [isDragging, setIsDragging] = useState(false);

// utilitaire commun (drop + file picker)
const handleDropZoneFiles = (fileList) => {
  const files = Array.from(fileList || []);
  const videos = files.filter((f) => f.type.startsWith('video/'));
  if (!videos.length) return;
  setAttachedFiles((prev) => [...prev, ...videos]);
  handleVideoUpload(videos[0]); // lance ton flux existant
};

// events dropzone
const onDrop = (e) => {
  e.preventDefault(); e.stopPropagation();
  setIsDragging(false);
  handleDropZoneFiles(e.dataTransfer.files);
};
const onDragOver = (e) => {
  e.preventDefault(); e.stopPropagation();
  e.dataTransfer.dropEffect = 'copy';
  setIsDragging(true);
};
const onDragLeave = (e) => {
  e.preventDefault(); e.stopPropagation();
  setIsDragging(false);
};


 
  //     onClick={() => handleEditInstruction(s.instruction)}   

return (
  <div className="min-h-screen bg-[#1d1d1f] text-zinc-100 flex flex-col">
    {/* Header */}
      <header className="sticky top-0 z-40 pt-5 pb-3 bg-[#1d1d1f]/80 backdrop-blur-lg border-b border-white/10 select-none">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          {/* Marque centrée */}
          <div className="flex-1 text-center leading-none" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <div className="mt-2 text-sm md:text-base uppercase tracking-[0.35em] text-white font-medium">
              AI VIDEO EDITOR
            </div>
          </div>
      
          {/* Toggle Beta */}
          <button
            onClick={() => setBetaMode(v => !v)}
            className={`ml-4 px-3 py-1.5 rounded-full text-xs font-medium border transition 
              ${betaMode 
                ? 'bg-emerald-500 text-black border-emerald-400' 
                : 'bg-zinc-800 text-white/80 border-white/20 hover:bg-zinc-700'
              }`}
          >
            Beta {betaMode ? 'ON' : 'OFF'}
          </button>
        </div>
      </header>


    {/* Messages */}
    <div className="flex-1 overflow-y-auto p-4" style={{ paddingBottom: bottomPad }}>
      <div className="max-w-4xl mx-auto space-y-6">
        {messages.map((message) => (
          <div key={message.id} className={`flex gap-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            {message.type === 'ai' && (
              <div className="w-10 h-10 flex items-center justify-center shrink-0">
                <span className="text-white/90 text-xl leading-none">✦</span>
              </div>
            )}

            <div className={`max-w-2xl rounded-2xl p-4 ${message.type === 'user' ? 'bg-white text-black' : 'bg-zinc-800 border border-zinc-700'}`}>
              {message.videoUrl && (
                <div className="mb-3 relative">
                  <video src={message.videoUrl} controls playsInline className="w-full rounded-lg" style={{ maxHeight: '300px' }} />
                  {message.isLocalFile && message.type === 'ai' && (
                    <button
                      onClick={() => downloadVideo(message.videoUrl, `edited-video-${Date.now()}.mp4`)}
                      className="absolute top-2 right-2 p-2 bg-black/70 rounded-lg text-white hover:bg-black/90 transition"
                      title="Télécharger la vidéo"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}

              <div className="whitespace-pre-line text-sm leading-relaxed">{message.content}</div>

              {/* SimpleVideoPreview pour les suggestions */}
              {message.videoPreview && (
                <div className="mt-4">
                    <SimpleVideoPreview
                      videoFile={message.videoPreview.file}
                      segments={message.videoPreview.segments}
                      title={message.videoPreview.plan?.title}
                      onExport={(viewport) =>
                        handleFinalExport(
                          message.videoPreview,
                          {
                            ...viewport,
                         //   addIntro: introOn,              // include intro?
                         //   addOutro: outroOn,              // include outro?
                            introUrl: INTRO_OUTRO_URL,
                            outroUrl: INTRO_OUTRO_URL,
                          }
                        )
                      }
                      isExporting={isProcessing}
                      defaultAddIntro={message.videoPreview.defaultAddIntro}
                      defaultAddOutro={message.videoPreview.defaultAddOutro}
                    />


                </div>
              )}

              {/* Si un audio est attaché, afficher un lecteur */}
              {message.audioUrl && (
                <div className="mt-3">
                  <audio src={message.audioUrl} controls className="w-full rounded-lg" />
                </div>
              )}

              {Array.isArray(message.suggestions) && message.suggestions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {message.suggestions.map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (s.instruction === '__TOGGLE_INTRO__') { setIntroOn(v => !v); return; }
                          if (s.instruction === '__TOGGLE_OUTRO__') { setOutroOn(v => !v); return; }
                          setInput(s.instruction);
                          inputRef.current?.focus();
                        }}
                        className={`px-3 py-1 text-sm rounded-full transition border ${getSuggestionClass(s)}`}
                        title={s.instruction}
                      >
                        {getSuggestionLabel(s)}
                      </button>
                    ))}

                </div>
              )}

              {message.editPlan && (
                <div className="mt-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-600">
                  <div className="text-xs text-zinc-400 mb-2 flex items-center gap-2">
                    <Zap className="w-3 h-3" />
                    Plan d'édition FFmpeg :
                  </div>
                  <div className="space-y-1">
                    {message.editPlan.segments?.map((seg, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="font-mono text-cyan-300">{seg.start} → {seg.end}</span>
                        {seg.label && <span className="ml-2 text-zinc-300">• {seg.label}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-2 text-xs opacity-50">{new Date(message.timestamp).toLocaleTimeString()}</div>
            </div>

            {message.type === 'user' && (
              <div className="w-10 h-10 bg-zinc-700 rounded-xl flex items-center justify-center shrink-0">
                <div className="w-6 h-6 bg-zinc-400 rounded-full"></div>
              </div>
            )}
          </div>
        ))}

        {/* Processing indicator avec progress */}
        {isProcessing && (
          <div className="flex gap-4 justify-start">
            <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="bg-zinc-800 border border-zinc-700 rounded-2xl p-4 min-w-[300px]">
              <div className="flex items-center gap-2 text-sm mb-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                <span>{processingStep}</span>
              </div>
              {processingProgress > 0 && (
                <div className="w-full bg-zinc-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>

    {/* Bloc bas : dropzone avant upload, textarea après upload */}
    <div
      ref={inputIslandRef}
      className="fixed bottom-6 left-4 right-4 z-50"
    >
      <div className="max-w-4xl mx-auto">
        {uploadedVideo ? (
          <> {/* ✅ Fragment ouvert */}
            {/* ====== INPUT quand une vidéo est présente ====== */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow transition-all duration-500">
              <div className="p-4">
                  {/* Ligne textarea + actions */}
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <input
                        ref={fileAttachRef}
                        type="file"
                        accept="video/*"
                        onChange={handleAttachPick}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        title="Ajouter une vidéo"
                      />
                      <button
                        onClick={() => fileAttachRef.current?.click()}
                        className="flex-shrink-0 w-10 h-10 bg-white hover:bg-gray-100 border border-black rounded-lg flex items-center justify-center transition-colors mt-1"
                        title="Ajouter une vidéo"
                      >
                        <Plus className="w-5 h-5 text-black" />
                      </button>
                    </div>
              
                    <div className="flex-1">
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Décris le montage que tu veux…"
                        className="w-full bg-transparent border-none focus:outline-none text-lg resize-none overflow-hidden text-black placeholder-gray-500"
                        rows={1}
                        style={{ minHeight: '24px', maxHeight: '144px' }}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e.target.scrollHeight, 144) + 'px';
                        }}
                        disabled={isProcessing || !ffmpegReady}
                      />
                    </div>
              
                    <button
                      onClick={handleSendMessage}
                      disabled={isProcessing || !ffmpegReady || !input.trim()}
                      className={`flex-shrink-0 w-10 h-10 bg-white hover:bg-gray-100 border border-black rounded-lg flex items-center justify-center transition-colors mt-1 ${!input.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Envoyer"
                    >
                      {isProcessing ? (
                        <div className="w-5 h-5 border-2 border-gray-400 border-t-black rounded-full animate-spin"></div>
                      ) : (
                        <Sparkles className="w-5 h-5 text-black" fill="currentColor" />
                      )}
                    </button>
                  </div>
              
                  {/* Barre de statut */}
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>{uploadedVideo.name} • Prêt</span>
                  </div>
                </div>
              </div>
            </div>
          </> /* ✅ Fragment fermé */
        ) : (
          <> {/* ✅ Fragment ouvert */}
            {/* ====== DROPZONE quand AUCUNE vidéo ====== */}
            <div
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); handleDropZoneFiles(e.dataTransfer.files); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
              onClick={() => fileAttachRef.current?.click()}
              className={`rounded-2xl border-2 border-dashed ${isDragging ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/30 bg-transparent'} shadow transition-all duration-300 p-6 flex items-center justify-center cursor-pointer`}
              style={{ minHeight: 72 }}
              title="Drop the video here"
            >
              {/* input fichier caché pour fallback au clic */}
              <input
                ref={fileAttachRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => handleDropZoneFiles(e.target.files)}
              />
              <div className="text-center select-none">
                <div className="text-sm md:text-base uppercase tracking-wide text-white/80">
                  Drop the video here
                </div>
                <div className="text-xs text-white/50 mt-1">
                  or click to browse
                </div>
              </div>
            </div>
          </> /* ✅ Fragment fermé */
        )}
      </div>
    </div>

    {/* Hidden file input (global) */}
    <input
      ref={fileInputRef}
      type="file"
      accept="video/*"
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) handleVideoUpload(file);
      }}
    />
  </div> /* <- ferme le root .min-h-screen */
);
}
